'use client';
import {useCallback, useRef, useEffect, ReactNode} from 'react';
import {useRouter} from 'next/navigation';
import {createPortal} from 'react-dom';

interface ModalProps {
  children: ReactNode;
  onDismiss?: () => void;
}

export default function Modal({children, onDismiss}: ModalProps) {
  const overlay = useRef<HTMLDivElement>(null);
  const wrapper = useRef<HTMLDivElement>(null);
  const router = useRouter();

  const onClose = useCallback(() => {
    if (onDismiss) return onDismiss();
    router.back();
  }, [router, onDismiss]);

  const onClick = useCallback(
    (e: MouseEvent) => {
      if (e.target === overlay.current || e.target === wrapper.current) {
        if (onClose) onClose();
      }
    },
    [onClose, overlay, wrapper]
  );

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onKeyDown]);

  return createPortal(
    <div
      ref={overlay}
      className='fixed inset-0 z-50 bg-black/60'
      //@ts-ignore
      onClick={onClick}
    >
      <div ref={wrapper} className='absolute inset-0 flex justify-center items-center'>
        {children}
      </div>
    </div>,
    document.getElementById('modal-root') as Element
  );
}
